import { useCallback, useState } from 'react';

import { StyledPage } from '../SignUp/styles';

import { logoWork } from '@/assets/img';
import Button from '@/components/Button';
import GravyWorkLogo from '@/components/GravyWorkLogo';
import Stack from '@/components/Stack';
import { Small, Title } from '@/components/Typography';
import Form from '@/form';
import FormFooter from '@/form/FormFooter';
import TextAreaField from '@/form/TextAreaField';
import TextField from '@/form/TextField';
import { axiosRubyClient } from '@/util/axios/axiosClient';

type FormValues = { email: string; reason: string };

const initialValues: FormValues = {
  email: '',
  reason: '',
};

const ENDPOINT = '/api/public/data_deletion_requests';

const DataDeletionRequest = () => {
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback(async (values: FormValues) => {
    setErrorMessage(null);
    setIsLoading(true);
    try {
      await axiosRubyClient.post(ENDPOINT, {
        email: values.email.trim(),
        reason: values.reason.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err: any) {
      const apiErrors: string[] | undefined = err?.response?.data?.errors;
      setErrorMessage(
        apiErrors && apiErrors.length > 0
          ? apiErrors.join(', ')
          : 'We could not submit your request. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <StyledPage css={{ alignItems: 'flex-start' }} size="xs">
      <Stack vertical gap={24}>
        <Stack vertical css={{ paddingTop: '66px' }} gap={24} justify="center">
          <GravyWorkLogo src={logoWork} />
          <Title weight={'normal'}>Request data deletion</Title>
          <Small color="default" size="md">
            Submit your email address below to request deletion of your personal
            data from GravyWork. Our team will follow up with you once the
            request has been processed.
          </Small>
        </Stack>

        {submitted ? (
          <Stack vertical gap={16}>
            <Title size="lg" weight="normal">
              Request received
            </Title>
            <Small color="default" size="md">
              Thanks — we&apos;ve recorded your data deletion request. Our team
              will reach out to the email you provided once the request has been
              processed.
            </Small>
          </Stack>
        ) : (
          <Form
            initialValues={initialValues}
            style={{ width: '100%' }}
            onSubmit={handleSubmit}
          >
            <Stack vertical gap={16}>
              <TextField
                autoFocus
                required
                autoComplete="email"
                fieldId="email"
                label="Email address"
                placeholder="you@example.com"
                type="email"
              />
              <TextAreaField
                fieldId="reason"
                label="Reason (optional)"
                placeholder="Tell us why you're requesting deletion (optional)"
              />
              {errorMessage && (
                <Small color="danger" size="md">
                  {errorMessage}
                </Small>
              )}
            </Stack>
            <FormFooter>
              <Button
                a11yLabel="Submit data deletion request"
                id="data-deletion-submit-btn"
                isLoading={isLoading}
                loadingLabel="Submitting..."
                style={{ width: '100%' }}
              />
            </FormFooter>
          </Form>
        )}
      </Stack>
    </StyledPage>
  );
};

export default DataDeletionRequest;
